import React, { useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { toast } from '../../toasts';
import moment from 'moment';
import GeneratedDebtService from '../../services/generatedDebts.service';
import GeneratedCreditService from '../../services/generatedCredits.service';
import BillService from '../../services/bill.service';
import { getAppEventEditTarget } from './calendarEventTypes';
import { getError } from '../../utils';
import SpinnerOverlay from '../SpinnerOverlay/SpinnerOverlay';
import { Button, FormField, controlClass } from '../EntityList';

/**
 * Date-only editor for app events (debt dues, credit dues, bills). Editing here
 * changes ONLY the single underlying record's date — it never touches sibling
 * dues/bills, and the change is one-way: it gets mirrored to Google Calendar
 * (if the user has sync enabled) but editing/deleting the mirror in Google
 * never comes back to the app.
 * Props:
 *   show: boolean
 *   onHide: () => void
 *   event: the calendar event object being edited (or null)
 *   onSaved: () => void — called after a successful save, so the parent can refetch
 */
const AppEventDateModal = ({ show, onHide, event, onSaved }) => {
	const [date, setDate] = useState('');
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (show && event) {
			setDate(moment.utc(event.date).format('YYYY-MM-DD'));
		}
	}, [show, event]);

	const handleSave = async () => {
		if (!date) { toast.error('Debe ingresar una fecha.'); return; }
		const target = event ? getAppEventEditTarget(event) : null;
		if (!target) { toast.error('Este evento no se puede editar.'); return; }

		const isoDate = moment.utc(date, 'YYYY-MM-DD').toISOString();

		setSaving(true);
		try {
			if (target.kind === 'debtDue') {
				await GeneratedDebtService.updateExpiration(target.id, isoDate);
			} else if (target.kind === 'creditDue') {
				await GeneratedCreditService.updateExpiration(target.id, isoDate);
			} else if (target.kind === 'bill') {
				await BillService.updateExpiration(target.id, isoDate);
			}
			toast.success('Fecha actualizada');
			onHide();
			onSaved();
		} catch (ex) {
			toast.error(getError(ex));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header className='tw:border-b tw:border-border tw:bg-surface-2' closeButton={!saving}>
				<Modal.Title className='tw:text-[15px] tw:font-semibold'>Editar Fecha</Modal.Title>
			</Modal.Header>
			<Modal.Body className='tw:relative tw:bg-surface tw:px-5 tw:py-5'>
				{saving && <SpinnerOverlay />}
				{event?.title && <p className="text-muted">{event.title}</p>}
				<Form onSubmit={(e) => e.preventDefault()}>
					<FormField id='app-event-date' label='Fecha' required>
						<input
							id='app-event-date'
							className={`${controlClass} tw:w-full`}
							type="date"
							value={date}
							onChange={(e) => setDate(e.target.value)}
							autoFocus
						/>
					</FormField>
				</Form>
			</Modal.Body>
			<Modal.Footer className='tw:flex tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2'>
				<Button onClick={onHide} disabled={saving}>Cancelar</Button>
				<Button variant='primary' onClick={handleSave} disabled={saving}>Guardar</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default AppEventDateModal;
